'use client';

import { useEffect } from 'react';
import { Locale } from '@headless-workspace/config';
import { DataLayerKey, isVideo, useDataLayer } from '@headless-workspace/domain/common/client';
import { CapacityFormat, ProductDetailValue, PromotionType } from '@headless-workspace/domain/common/server';
import { Component } from '@headless-workspace/typings';
import { ValueHelpers } from '@headless-workspace/utils';

type ProductDetailTemplateDidMountProps = {
    locale: Locale;
    product: ProductDetailValue;
};

export const ProductDetailTemplateDidMount: Component<ProductDetailTemplateDidMountProps> = ({ locale, product }) => {
    const { addVariable, addVariables } = useDataLayer();

    const brandName = product.brand.name;

    const productBreadcrumbId = product.breadcrumb.map((elem) => elem.id);

    const hasDiscount = ValueHelpers.mapYesNo(!!product.priceBeforeDiscount);

    const isCustomizable = ValueHelpers.mapYesNo(!!product.isEngravable);

    const inStock = ValueHelpers.mapYesNo(!!product.stockLevel);

    const hasTravelSize = ValueHelpers.mapYesNo(!!product.formats.includes(CapacityFormat.Mini));

    const hasVideo = ValueHelpers.mapYesNo(!!product.medias.some((media) => isVideo(media)));

    const HasDefaultSku = ValueHelpers.mapYesNo(!!product.masterProductId);

    const productPromotion = product.promotion === PromotionType.OTHER ? 'without promotion' : 'crossed out price';

    const calculateDiscountAti = (originalPrice?: number, priorPrice?: number) => {
        return priorPrice && originalPrice ? originalPrice - priorPrice : 0;
    };

    const getProductNumRating = (reviewCount: number) => {
        if (reviewCount === 0) return '0';

        if (reviewCount <= 10) return '0-10';

        if (reviewCount <= 25) return '11-25';

        if (reviewCount <= 50) return '26-50';

        if (reviewCount <= 75) return '51-75';

        return '76+';
    };

    useEffect(() => {
        if (typeof document !== 'undefined') {
            addVariables({
                [DataLayerKey.PageCat]: 'page_cat', // FIX ME: TBC
                [DataLayerKey.PageName]: `${product.name?.full}|${product.id}`,
                [DataLayerKey.PageSubCat]: 'page_sub_cat', // FIX ME: TBC
                [DataLayerKey.PageTopCat]: 'page_top_cat', // FIX ME: TBC
                [DataLayerKey.PageTrademark]: brandName,
                [DataLayerKey.ProductAxis]: 'product_axis', // FIX ME: TBC
                [DataLayerKey.ProductBrand]: brandName,
                [DataLayerKey.ProductBreadcrumbId]: productBreadcrumbId.toString(),
                [DataLayerKey.ProductBreadcrumbLabel]: 'product_breadcrumb_label', // FIX ME: TBC
                [DataLayerKey.ProductCop]: hasDiscount,
                [DataLayerKey.ProductCurrency]: product.currency.toLowerCase(),
                [DataLayerKey.ProductCustomizable]: isCustomizable,
                [DataLayerKey.ProductCustomized]: 'product_customized', // FIX ME: TBC
                [DataLayerKey.ProductDefaultSku]: HasDefaultSku,
                [DataLayerKey.ProductDiscountAti]: calculateDiscountAti(
                    product.originalPrice,
                    product.priorPrice,
                ).toString(),
                [DataLayerKey.ProductDiscountTf]: 'product_discount_tf', // FIX ME: TBC
                [DataLayerKey.ProductFidelityPoints]: 'product_fidelity_points', // FIX ME: TBC
                [DataLayerKey.ProductInstock]: inStock,
                [DataLayerKey.ProductMarket]: 'product_market', // FIX ME: TBC
                [DataLayerKey.ProductNature]: 'product_nature', // FIX ME: TBC
                [DataLayerKey.ProductNumRating]: getProductNumRating(product.reviewCount),
                [DataLayerKey.ProductOldPriceAti]: product.priorPrice?.toString(),
                [DataLayerKey.ProductOldPriceTf]: product.vat?.toString(),
                [DataLayerKey.ProductPid]: product.masterProductId,
                [DataLayerKey.ProductPidName]: product.name?.full,
                [DataLayerKey.ProductPriceAti]: product.priorPrice?.toString(),
                [DataLayerKey.ProductPriceTf]: product.vat?.toString(),
                [DataLayerKey.ProductPromotion]: productPromotion,
                [DataLayerKey.ProductRange]: 'product_range', // FIX ME: TBC
                [DataLayerKey.ProductRating]: product.rating.toString(),
                [DataLayerKey.ProductSection]: 'product_section', // FIX ME: TBC
                [DataLayerKey.ProductSku]: product.sku,
                [DataLayerKey.ProductSkuCoverage]: 'product_sku_coverage', // FIX ME: TBC
                [DataLayerKey.ProductSkuName]: 'product_sku_name', // FIX ME: TBC
                [DataLayerKey.ProductSubbrand]: 'product_subbrand', // FIX ME: TBC
                [DataLayerKey.ProductTarget]: 'product_target', // FIX ME: TBC
                [DataLayerKey.ProductTrademark]: brandName,
                [DataLayerKey.ProductTravelSizeAvailability]: hasTravelSize,
                [DataLayerKey.ProductUrlPicture]: 'product_url_picture', // FIX ME: TBC
                [DataLayerKey.ProductVideo]: hasVideo,
            });
        }
    }, [addVariable, DataLayerKey, product]);

    return null;
};
